import { Box } from '@mui/material';
import { ReactElement } from 'react';
import {
  DataTable,
  FunctionField,
  ListContextProvider,
  ResourceContextProvider,
  useList,
  type SortPayload,
} from 'react-admin';
import {
  reportDataTableSx,
  type ReportGranularity,
} from '../../../../Components/ReportsWidget/utils';
import type { ClicksSummariesResource } from '../../../../Entities/ClicksSummary';
import ClickCountLink from './ClickCountLink';
import ClicksSummaryChart, { type ClicksSummaryChartZoomState } from './ClicksSummaryChart';
import ClicksSummaryTotalsBody from './ClicksSummaryTotalsBody';
import { FUTURE_REFNAMES, GETPRICE_REFNAME, formatReportDate } from './utils';

export interface ClicksSummaryDataTableProps {
  data: ClicksSummariesResource[];
  isPending?: boolean;
  sort?: SortPayload;
  granularity: ReportGranularity;
  zoomState: ClicksSummaryChartZoomState;
  onZoomStateChange: (zoomState: ClicksSummaryChartZoomState) => void;
}

export default function ClicksSummaryDataTable({
  data,
  isPending = false,
  sort,
  granularity,
  zoomState,
  onZoomStateChange,
}: ClicksSummaryDataTableProps): ReactElement {
  const listContext = useList<ClicksSummariesResource>({
    data,
    isPending,
    sort,
    perPage: Math.max(data.length, 1),
  });

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <ListContextProvider value={listContext}>
        <ResourceContextProvider value="clicks_summaries">
          {!isPending && (
            <ClicksSummaryChart
              granularity={granularity}
              onZoomStateChange={onZoomStateChange}
              zoomState={zoomState}
            />
          )}
          <DataTable
            rowClick={false}
            bulkActionButtons={false}
            size="small"
            sx={reportDataTableSx}
            body={ClicksSummaryTotalsBody}
          >
            <DataTable.Col source="attributes.date" label="Date">
              <FunctionField
                render={(record) => formatReportDate(record.attributes?.date, granularity)}
              />
            </DataTable.Col>
            <DataTable.Col source="attributes.total_clicks" label="Total Clicks">
              <FunctionField
                render={(record) => (
                  <ClickCountLink record={record} source="total_clicks" granularity={granularity} />
                )}
              />
            </DataTable.Col>
            <DataTable.Col source="attributes.getprice_clicks" label="Getprice Clicks">
              <FunctionField
                render={(record) => (
                  <ClickCountLink
                    record={record}
                    source="getprice_clicks"
                    granularity={granularity}
                    refnameFilter={{ kind: 'contains', values: [GETPRICE_REFNAME] }}
                  />
                )}
              />
            </DataTable.Col>
            <DataTable.Col source="attributes.future_clicks" label="Future Clicks">
              <FunctionField
                render={(record) => (
                  <ClickCountLink
                    record={record}
                    source="future_clicks"
                    granularity={granularity}
                    refnameFilter={{ kind: 'contains', values: FUTURE_REFNAMES }}
                  />
                )}
              />
            </DataTable.Col>
            <DataTable.Col source="attributes.other_clicks" label="Other Clicks">
              <FunctionField
                render={(record) => (
                  <ClickCountLink
                    record={record}
                    source="other_clicks"
                    granularity={granularity}
                    refnameFilter={{
                      kind: 'doesNotContain',
                      values: [GETPRICE_REFNAME, ...FUTURE_REFNAMES],
                    }}
                  />
                )}
              />
            </DataTable.Col>
          </DataTable>
        </ResourceContextProvider>
      </ListContextProvider>
    </Box>
  );
}
